import { useParams } from '@tanstack/react-router'
import { ApiError } from '../api/client'
import { usePublicSeason } from '../api/hooks'
import type { PublicSeasonSummary } from '../api/types'
import { Pill, Wordmark } from '../primitives'
import { formatSeasonDateRange, formatStandingMetric, winnerCalcLabel } from '../seasons/format'

export const PublicSeasonScreen = () => {
  const { token } = useParams({ from: '/s/season/$token' })
  const q = usePublicSeason(token)

  if (q.isLoading) {
    return (
      <Frame>
        <div style={{ color: 'var(--ink-3)', padding: 16 }}>Loading…</div>
      </Frame>
    )
  }
  if (q.error) {
    const err = q.error
    const status = err instanceof ApiError ? err.status : null
    return (
      <Frame>
        <div
          style={{
            padding: 24,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            color: 'var(--ink-2)',
          }}
        >
          <div style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic', fontSize: 22 }}>
            {status === 410 ? 'This share link has been revoked.' : 'Season not found.'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
            Ask the season admin for a fresh link if one is still available.
          </div>
        </div>
      </Frame>
    )
  }
  if (!q.data) return null

  return (
    <Frame>
      <Body summary={q.data} />
    </Frame>
  )
}

const Frame = ({ children }: { children: React.ReactNode }) => (
  <div className="paper" style={{ minHeight: '100vh' }}>
    <header
      style={{
        padding: '14px 16px',
        borderBottom: '1px solid var(--line-soft)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Wordmark size={22} />
      <div className="kicker" style={{ fontSize: 10 }}>
        public summary
      </div>
    </header>
    <main>{children}</main>
  </div>
)

const Body = ({ summary }: { summary: PublicSeasonSummary }) => (
  <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 18 }}>
    <div>
      <div className="kicker" style={{ fontSize: 11 }}>
        {summary.groupName}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 700,
          fontSize: 24,
          marginTop: 2,
        }}
      >
        {summary.seasonName}
      </div>
      <div style={{ marginTop: 6, fontSize: 13, color: 'var(--ink-2)' }}>
        {formatSeasonDateRange(summary.startAt, summary.endAt)} ·{' '}
        {winnerCalcLabel(summary.winnerCalcMethod)}
        {summary.winnerCalcMethod === 'highest_win_pct' &&
          summary.minPlaysThreshold !== null &&
          ` (min ${summary.minPlaysThreshold} plays)`}
      </div>
    </div>

    {summary.includedGames.length > 0 && (
      <div>
        <Heading>Included games</Heading>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {summary.includedGames.map((g) => (
            <Pill key={g.id} color="slate">
              {g.name}
            </Pill>
          ))}
        </div>
      </div>
    )}

    <div>
      <Heading>Standings</Heading>
      {summary.standings.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>No qualifying plays yet.</div>
      ) : (
        <div
          style={{
            background: 'var(--paper)',
            border: '1px solid var(--line-soft)',
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          {summary.standings.map((s) => (
            <div
              key={`${s.handle}-${s.rank}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 14px',
                borderBottom: '1.5px dashed var(--paper-edge)',
                opacity: s.qualified ? 1 : 0.55,
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 12,
                  color: 'var(--ink-3)',
                  minWidth: 24,
                }}
              >
                {String(s.rank).padStart(2, '0')}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{s.displayName}</div>
                <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                  @{s.handle}
                  {!s.qualified && ' · below threshold'}
                </div>
              </div>
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--ink)',
                }}
              >
                {formatStandingMetric(summary.winnerCalcMethod, s)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>

    <div style={{ fontSize: 11, color: 'var(--ink-3)', textAlign: 'center', paddingTop: 16 }}>
      Read-only summary. No play details or avatars are shown on shared links.
    </div>
  </div>
)

const Heading = ({ children }: { children: React.ReactNode }) => (
  <div className="kicker" style={{ marginBottom: 6 }}>
    {children}
  </div>
)
